import {useEffect, useRef, useState} from 'react';

import type {AiSuggestion} from '../types';

const FLASH_DURATION_MS = 1200;

/** Field names present in `aiFields` that aren't in `seen` yet — the pure diffing step. */
export function findNewlyFilledFields(
  aiFields: Map<string, AiSuggestion>,
  seen: ReadonlySet<string>
): string[] {
  return Array.from(aiFields.keys()).filter(field => !seen.has(field));
}

/**
 * Tracks which fields just transitioned from unfilled to AI-filled, for a brief highlight
 * animation that draws the eye back to fields sitting above the just-uploaded document.
 */
export function useAiFillFlash(aiFields: Map<string, AiSuggestion>): (field: string) => boolean {
  const [flashingFields, setFlashingFields] = useState<Set<string>>(() => new Set());
  const seenRef = useRef<Set<string>>(new Set());
  const timersRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  useEffect(() => {
    const newlySeen = findNewlyFilledFields(aiFields, seenRef.current);
    if (newlySeen.length === 0) return;
    newlySeen.forEach(field => seenRef.current.add(field));

    setFlashingFields(prev => new Set([...prev, ...newlySeen]));
    // Each batch's timer is independent of later effect runs — cancelling it whenever `aiFields`
    // changes again (the old per-run cleanup) would leave an earlier batch flashing forever if a
    // second batch arrives before the first timer fires. Timers are only swept on unmount.
    const timer = setTimeout(() => {
      timersRef.current.delete(timer);
      setFlashingFields(prev => {
        const next = new Set(prev);
        newlySeen.forEach(field => next.delete(field));
        return next;
      });
    }, FLASH_DURATION_MS);
    timersRef.current.add(timer);
  }, [aiFields]);

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach(timer => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  return (field: string) => flashingFields.has(field);
}
